import React, { useMemo, useOptimistic, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/chariot-ui';
import { ToggleSwitch } from '@/components/ui';
import { useMy } from '@/hooks';
import { useUpdateFlags } from '@/hooks/useFlags';
import { SettingActionCard } from '@/sections/settings/components';
import { dangerButtonStyle } from '@/sections/settings/constants';
import { useAuth } from '@/state/auth';
import { ChariotModuleFlags, FlagType } from '@/types';

const chariotModuleOptions = [
  {
    name: 'Static IP Addresses',
    description: 'Enables scanning from a static IP pool',
    value: FlagType.DISABLE_STATIC_IP_ADDRESSES,
  },
  {
    name: 'AI Training',
    description: 'Enables AI model training on account data',
    value: FlagType.DISABLE_AIMI_TRAINING,
  },
  {
    name: 'AI Inference',
    description:
      'Enables AI inference features (AI Assistant, agent capabilities)',
    value: FlagType.DISABLE_AI_INFERENCE,
  },
];

interface PraetorianFeaturesSectionProps {
  email?: string;
  editable?: boolean;
  onDeleteAccount: () => void;
}

/**
 * Praetorian-only features section with feature flag toggles and account deletion.
 *
 * Features:
 * - Feature flag toggles (Static IP, AI Training)
 * - useOptimistic for instant toggle feedback with automatic rollback
 * - Delete account button (danger action)
 * - Only visible to Praetorian users and admin@praetorian.com
 * - Impersonation support via email prop
 *
 * Feature Flags:
 * - Static IP Addresses: Controls static IP pool for scanning
 * - AI Training: Controls AI model training on account data
 *
 * Special Handling:
 * - Static IP flag toggles both DISABLE_STATIC_IP_ADDRESSES and 'static' flags
 * - Flags are stored as disabled flags (inverted logic: flag present = feature disabled)
 *
 * React 19 Patterns:
 * - useOptimistic: Instant UI updates with automatic rollback on error
 * - Manual memoization for server flags (simple array filter, but needed for useOptimistic)
 *
 * Used in:
 * - OrganizationSettingsCards (conditional rendering for Praetorian users)
 */
export const PraetorianFeaturesSection: React.FC<
  PraetorianFeaturesSectionProps
> = ({ email, editable = true, onDeleteAccount }) => {
  const { me, friend } = useAuth();
  const currentEmail = email || friend || me;

  // Feature Flags (Praetorian only)
  const { data: flagsData, isLoading: isLoadingFlags } = useMy(
    {
      resource: 'flag',
    },
    {
      email: currentEmail,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const { mutateAsync: updateFlags } = useUpdateFlags({
    email: currentEmail,
  });

  // Server state: Extract ChariotModuleFlags from API response
  // Memoization needed for useOptimistic dependency (not for performance)
  const serverFlags = useMemo(
    () =>
      (flagsData || [])
        .map(flag => flag.name)
        .filter(name => ChariotModuleFlags.includes(name as FlagType)),
    [flagsData]
  );

  // useOptimistic: Instant UI updates with automatic rollback on error
  // Second param is reducer that receives (currentState, optimisticValue)
  const [optimisticFlags, setOptimisticFlags] = useOptimistic(
    serverFlags,
    (_currentState, optimisticValue: string[]) => optimisticValue
  );

  const [updatingFlag, setUpdatingFlag] = useState<string | null>(null);

  const handleToggleFlag = async (
    checked: boolean,
    value: string,
    name: string
  ) => {
    setUpdatingFlag(value);

    try {
      const newFlags = checked
        ? optimisticFlags.filter(flag => flag !== value)
        : [...optimisticFlags, value];

      // Optimistic update - useOptimistic will auto-rollback on error
      setOptimisticFlags(newFlags);

      const flagsToAdd = !checked ? [value] : [];
      const flagsToDelete = checked ? [value] : [];

      // Static IP special handling: Toggle both DISABLE_STATIC_IP_ADDRESSES and 'static'
      if (value === FlagType.DISABLE_STATIC_IP_ADDRESSES) {
        if (checked) {
          flagsToAdd.push('static');
        } else {
          flagsToDelete.push('static');
        }
      }

      await updateFlags({
        flagsToAdd,
        flagsToDelete,
        silent: true,
      });

      toast.success(`${name} has been ${checked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(`Failed to update ${name}. Please try again.`);
      console.error('Failed to update flag:', error);
    } finally {
      setUpdatingFlag(null);
    }
  };

  return (
    <>
      {/* Feature Toggle Cards */}
      {chariotModuleOptions.map(({ name, description, value }) => {
        const isUpdating = updatingFlag === value;
        // Inverted logic: Flag present = feature disabled, flag absent = feature enabled
        const isEnabled = !optimisticFlags.includes(value);

        return (
          <SettingActionCard
            key={value}
            data-testid={`feature-${value}-card`}
            action={
              <ToggleSwitch
                id={`feature-${value}`}
                checked={isEnabled}
                onChange={checked => handleToggleFlag(checked, value, name)}
                disabled={!editable || isLoadingFlags || isUpdating}
              />
            }
          >
            <div className="flex items-center gap-2">
              <h4 className="font-bold">{name}</h4>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-chariot-text-secondary text-xs">
                {description}
              </span>
            </div>
          </SettingActionCard>
        );
      })}

      {/* Account Deletion Card (Danger) */}
      <SettingActionCard
        data-testid="account-deletion-card"
        action={
          editable ? (
            <Button
              onClick={onDeleteAccount}
              label="Delete Account"
              className={dangerButtonStyle}
              size="xs"
              data-testid="delete-account-button"
            />
          ) : undefined
        }
      >
        <div className="flex flex-col gap-2">
          <h4 className="font-bold text-red-600">Delete Account</h4>
          <span className="text-chariot-text-secondary text-xs">
            Permanently delete this account and all associated data
          </span>
        </div>
      </SettingActionCard>
    </>
  );
};
